import React, { useState, useEffect, useRef } from 'react';
import api from '../../hooks/useApi';
import SowSelect from './SowSelect';
import OverheadCategorySelect from './OverheadCategorySelect';
import { dailyCap, canClaimOvertime, splitOvertime, workedHours } from '../../utils/overtime';
import { isOtherOverhead, OTHER_NOTE_MESSAGE } from '../../utils/overheads';
import {
  groupMilestonesForPicker,
  milestoneExpected,
  missingMilestoneReason,
  NO_MILESTONE_REASON_MESSAGE,
} from '../../utils/milestoneLogging';
import { earliestEditable } from '../../utils/timeLock';
import { canAccess } from '../../utils/rbac';
import useTimeFavourites from '../../hooks/useTimeFavourites';
import FavouritesBar from './FavouritesBar';

const fmtH = (n) => (n % 1 === 0 ? `${n}h` : `${n.toFixed(2).replace(/0$/, '')}h`);
const weekdayLong = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' }) : '';

// Overtime capture is temporarily disabled per business request. While false,
// hours over the daily cap are simply logged as normal time and the overtime
// split/approval prompt never appears. Flip to true to re-enable it.
const OVERTIME_ENABLED = false;

// A not-yet-issued SOW has no formal engagement yet — time logged against it
// is bid/SOW-creation work, not billable client work. Mirrors the same
// auto-tagging behaviour in the full Add Time modal (MyTimePage.jsx) — kept
// in sync manually since the two components don't share this logic.
const BID_SOW_CREATION_CATEGORY_CODE = 'BD_BID';

export default function QuickTimeLog({ user, variant = 'float' }) {
  const inSidebar = variant === 'sidebar';
  const [open, setOpen]         = useState(false);
  const [minimized, setMinimized] = useState(() => sessionStorage.getItem('qtl_min') === '1');

  const minimize = () => { setMinimized(true);  sessionStorage.setItem('qtl_min', '1'); };
  const restore  = () => { setMinimized(false); sessionStorage.removeItem('qtl_min'); };
  const [tasks, setTasks]       = useState([]);
  const [overheads, setOverheads] = useState([]);
  const [sows, setSows]         = useState([]);
  const [mode, setMode]         = useState('task'); // 'task' | 'sow' | 'overhead'
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedSow, setSelectedSow]   = useState('');
  const [selectedOverhead, setSelectedOverhead] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  // Deliberately not pre-filled — defaulting to "1" made the cap/overtime
  // preview below silently assume you were about to log another hour the
  // moment you opened the panel, which read as "8.5h / 7.5h — over!" before
  // you'd chosen anything at all. Blank until you actually pick a value.
  const [hours, setHours]       = useState('');
  const [notes, setNotes]       = useState('');
  const [date, setDate]         = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage]   = useState('');
  const [todayHours, setTodayHours] = useState(null);
  const [dayHours, setDayHours] = useState(0);   // hours already logged on the selected date
  const [otConfirm, setOtConfirm] = useState(null); // { normalPart, overtimePart } awaiting confirmation
  const panelRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  // A week closes when the following Monday ends, slid over bank holidays; the
  // picker is floored so a closed date cannot be chosen and then refused by the
  // server. Holders of time:manage bypass, the key the server's canManageTime
  // and the My Time page ask. Until the holidays load (fetched when the panel opens),
  // the plain-Monday rule applies, which only ever floors LATER than the real
  // rule would allow — never the reverse.
  const lockBypass = canAccess('time:manage');
  const [recentBankHols, setRecentBankHols] = useState(() => new Set());
  useEffect(() => {
    if (!open) return;
    const from = new Date(Date.now() - 21 * 86400000).toISOString().slice(0, 10);
    api.get('bank-holidays', { from, to: today })
      .then(rows => setRecentBankHols(new Set(
        (rows || []).filter(h => h.kind === 'bank_holiday').map(h => h.holiday_date)
      )))
      .catch(() => {});
  }, [open]);
  const editCutoff = earliestEditable(today, (d) => recentBankHols.has(d));

  const cap = dailyCap(user);
  const eligible = OVERTIME_ENABLED && canClaimOvertime(user);
  const { favourites, sowFavourites, has: isFav, toggle: toggleFav } = useTimeFavourites(user?.id);

  // Current target (for the "Favourite this" affordance and one-tap re-pick).
  const current = mode === 'task' && selectedTask ? { kind: 'task', ref_id: String(selectedTask) }
    : mode === 'sow' && selectedSow ? { kind: 'sow', ref_id: String(selectedSow) }
    : mode === 'overhead' && selectedOverhead ? { kind: 'overhead', ref_id: String(selectedOverhead) }
    : null;
  const pickFavourite = (fav) => {
    if (fav.kind === 'task') { setMode('task'); setSelectedTask(fav.ref_id); }
    else if (fav.kind === 'sow') { setMode('sow'); setSelectedSow(fav.ref_id); }
    else { setMode('overhead'); setSelectedOverhead(fav.ref_id); }
  };
  const currentLabel = !current ? ''
    : current.kind === 'task' ? (tasks.find(t => String(t.id) === current.ref_id)?.name || '')
    : current.kind === 'sow'  ? (sows.find(s => String(s.id) === current.ref_id)?.reference || '')
    : (overheads.find(o => String(o.id) === current.ref_id)?.name || '');

  const refreshTodayHours = () => {
    if (!user?.id) return;
    api.get('time-entries', { person_id: user.id, from: today, to: today })
      .then(entries => {
        // Hours worked: the entries approved leave writes are not work, and
        // counting them would put a day off past the cap.
        setTodayHours(workedHours(Array.isArray(entries) ? entries : []));
      })
      .catch(() => setTodayHours(null));
  };

  // Hours already logged on the *selected* date (drives the cap / overtime preview)
  const refreshDayHours = (d) => {
    if (!user?.id || !d) { setDayHours(0); return; }
    api.get('time-entries', { person_id: user.id, from: d, to: d })
      .then(entries => {
        setDayHours(workedHours(Array.isArray(entries) ? entries : []));
      })
      .catch(() => setDayHours(0));
  };

  // ── The day may already be accounted for (El's item 44) ────────────────────
  //
  // "Can this link up with sickness and annual leave so it automatically applies
  // absences?" — filling the day "without counting as work".
  //
  // Nothing is written: a day off is not hours, and logging it as hours would put
  // holiday into utilisation and into project cost. What the panel does instead is
  // say the day is covered, so the person is not left wondering what to log
  // against a day they were not here. Logging against it is still possible —
  // people do work on a bank holiday — it is just no longer the assumption.
  const [dayAbsence, setDayAbsence] = useState(null);
  useEffect(() => {
    if (!user?.id || !date) { setDayAbsence(null); return; }
    api.get('absences', { person_id: user.id, from: date, to: date })
      .then(rows => setDayAbsence((rows || [])[0] || null))
      .catch(() => setDayAbsence(null));
  }, [user?.id, date]);

  useEffect(() => { refreshTodayHours(); }, [user?.id]);
  // Keep the "today" badge (and open-panel day total) in step when time is
  // logged anywhere else (e.g. the My Time page).
  useEffect(() => {
    const h = () => { refreshTodayHours(); if (open) refreshDayHours(date); };
    window.addEventListener('av:time-entries-changed', h);
    return () => window.removeEventListener('av:time-entries-changed', h);
  }, [open, date, user?.id]);
  useEffect(() => { if (open) refreshDayHours(date); setOtConfirm(null); }, [open, date, user?.id]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('tasks/mine').catch(() => []),
      api.get('overhead-categories').catch(() => []),
      api.get('sows', { status: 'Active' }).catch(() => []),
      // Draft SOWs are included too — lets bid/SOW-creation time be tracked
      // against the SOW before it's issued (see isDraftSow below).
      api.get('sows', { status: 'Draft' }).catch(() => []),
      // Awaiting Payment too: invoice queries and snagging still take time.
      api.get('sows', { status: 'Awaiting Payment' }).catch(() => []),
    ]).then(([t, o, active, draft, awaitingPayment]) => {
      setTasks(Array.isArray(t) ? t : []);
      setOverheads(Array.isArray(o) ? o : []);
      setSows([active, draft, awaitingPayment].flatMap(x => (Array.isArray(x) ? x : [])));
    });
  }, [open]);

  // Close on outside click — skip if target was removed from DOM by a re-render
  // (e.g. SowSelect closing its dropdown after a selection)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!document.body.contains(e.target)) return;
      // A guided tour walks through this panel, and its overlay is portalled to
      // <body> rather than rendered inside the panel. Without this, pressing
      // Next counted as clicking away: the panel shut under the very step
      // describing it, and the next step's before hook reopened it.
      if (e.target.closest?.('[data-feature-tour]')) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedSowObj = mode === 'sow' ? sows.find(s => String(s.id) === String(selectedSow)) : null;
  const isDraftSow = selectedSowObj?.status === 'Draft';
  const bidSowCategoryId = overheads.find(o => o.code === BID_SOW_CREATION_CATEGORY_CODE)?.id || '';

  // Milestones for the chosen project, so an hour can be costed against the part
  // of the job it was actually for. A Draft SOW has no milestones to bill yet —
  // its time is Bid & SOW Creation — so nothing is fetched for one.
  useEffect(() => {
    if (mode !== 'sow' || !selectedSow || isDraftSow) { setMilestones([]); return; }
    let live = true;
    api.get('milestones', { sow_id: selectedSow })
      .then(m => { if (live) setMilestones(Array.isArray(m) ? m : []); })
      .catch(() => { if (live) setMilestones([]); });
    return () => { live = false; };
  }, [mode, selectedSow, isDraftSow]);

  // A milestone belongs to one project, so it cannot survive a change of target.
  useEffect(() => { setSelectedMilestone(''); }, [mode, selectedSow]);

  const { open: openMilestones, done: doneMilestones } = groupMilestonesForPicker(milestones);
  const milestoneLabel = (m) => `${m.milestone_code || 'Milestone'}${m.description ? ` — ${m.description.slice(0, 40)}` : ''}`;
  // The project has milestones and none is picked. Said while the panel is still
  // open and the answer is still known, rather than left for the Time Entry
  // Audit to raise with somebody else weeks later.
  const milestoneUnset = mode === 'sow' && !isDraftSow && milestoneExpected(milestones) && !selectedMilestone;
  const needsMilestoneReason = milestoneUnset && missingMilestoneReason({ milestones, milestoneId: selectedMilestone, notes });

  const targetPayload = () => (
    mode === 'task'     ? { task_id: selectedTask }
    : mode === 'sow'    ? { sow_id: selectedSow, milestone_id: selectedMilestone || null, ...(isDraftSow ? { overhead_category_id: bidSowCategoryId, billable: false } : {}) }
    :                     { overhead_category_id: selectedOverhead }
  );

  // Is the chosen overhead an "Other" bucket? The rule itself lives in
  // utils/overheads.js so the full Add Entry form applies exactly the same one.
  const isOther = mode === 'overhead' && isOtherOverhead(overheads, selectedOverhead);

  const validate = () => {
    const h = parseFloat(hours);
    if (!h || h <= 0 || h > 24) { setMessage('Enter a valid number of hours (0.25 – 24)'); return null; }
    if (mode === 'task' && !selectedTask) { setMessage('Please select a task'); return null; }
    if (mode === 'sow' && !selectedSow)   { setMessage('Please select a SOW'); return null; }
    if (mode === 'overhead' && !selectedOverhead) { setMessage('Please select a category'); return null; }
    // Item 42. "Other" tells a reader nothing on its own, and an hour a month
    // later cannot be explained by anybody. Say what it was.
    if (isOther && !notes.trim()) {
      setMessage(OTHER_NOTE_MESSAGE);
      return null;
    }
    // Hours on a milestoned project with no milestone cost against nothing. If
    // that is deliberate, say why, so the entry explains itself to whoever reads
    // the audit instead of becoming their guesswork.
    if (needsMilestoneReason) {
      setMessage(NO_MILESTONE_REASON_MESSAGE);
      return null;
    }
    return h;
  };

  // Post one or more entries sequentially, then reset + close
  const postEntries = async (entries, summary) => {
    setSubmitting(true);
    setMessage('');
    try {
      const base = { date, description: notes.trim() || null, source: 'quick-log', ...targetPayload() };
      for (const extra of entries) await api.post('time-entries', { ...base, ...extra });
      setMessage(summary);
      // Item 40: the panel stays open so several entries can be logged in one
      // go — a day is rarely one thing. Hours and notes clear because they
      // belong to the entry just filed; the target (task / SOW / category) and
      // the date stay, since the next entry is usually the same day and often
      // the same job. Only the confirmation message is on a timer.
      setHours(''); setNotes(''); setOtConfirm(null);
      refreshTodayHours(); refreshDayHours(date);
      // Let any open time views (My Time grid, other loggers) refresh instantly.
      window.dispatchEvent(new CustomEvent('av:time-entries-changed'));
      setTimeout(() => setMessage(''), 2500);
    } catch (e) {
      setMessage(e.message || 'Failed to log time');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const h = validate();
    if (h == null) return;
    const { normalPart, overtimePart, overCap } = splitOvertime(dayHours, h, cap);
    // Eligible + over the daily cap → confirm the split before logging
    if (overCap && eligible) { setOtConfirm({ normalPart, overtimePart }); setMessage(''); return; }
    // Otherwise (within cap, or not eligible to claim) → log as a single normal entry
    postEntries([{ hours: h }], `Logged ${fmtH(h)}`);
  };

  // Confirmed: split into normal + overtime, raising a pending overtime request
  const confirmSplit = () => {
    const { normalPart, overtimePart } = otConfirm;
    const entries = [];
    if (normalPart > 0) entries.push({ hours: normalPart });
    entries.push({ hours: overtimePart, is_overtime: true, submit_overtime_request: true });
    postEntries(entries, `Logged ${fmtH(normalPart)} + ${fmtH(overtimePart)} overtime (sent for approval)`);
  };

  // Confirmed: ignore the cap and log everything as normal time
  const confirmAllNormal = () => {
    const h = parseFloat(hours);
    postEntries([{ hours: h }], `Logged ${fmtH(h)}`);
  };

  const quickHours = [0.5, 1, 1.5, 2, 3, 4];
  // "Rest of day" — the actual gap to this user's cap on the selected date, so
  // logging 1h here + 1h there and wanting the remainder against a third thing
  // doesn't require doing the subtraction by hand.
  const remainder = Math.max(0, Math.round((cap - dayHours) * 4) / 4);

  // Live cap / overtime preview for the selected date
  const hNum = parseFloat(hours) || 0;
  const preview = splitOvertime(dayHours, hNum, cap);
  const capPct = Math.min(100, Math.round((dayHours / cap) * 100));

  const hoursBadge = todayHours !== null ? `${todayHours % 1 === 0 ? todayHours : todayHours.toFixed(1)}h` : null;

  const panelClass = inSidebar
    ? 'fixed bottom-6 left-6 md:left-[208px] z-50 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden'
    : 'fixed bottom-28 left-6 md:left-[216px] z-50 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden';

  return (
    <>
      {/* Trigger — docked in the navy sidebar (app shell default) or floating */}
      {inSidebar ? (
        <button
          data-tour="quick-time-log"
          onClick={() => setOpen(o => !o)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-md transition-colors ${open ? 'bg-white/10' : 'hover:bg-white/5'}`}
          title="Quick Time Log"
          aria-label="Log time"
        >
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </span>
          <span className="text-[11px] font-semibold text-white/90">Log time</span>
          {hoursBadge && (
            <span className="ml-auto text-[9px] font-bold text-gold-light bg-white/10 rounded-full px-1.5 py-0.5">{hoursBadge} today</span>
          )}
        </button>
      ) : minimized ? (
        <div className="fixed bottom-6 left-6 md:left-[216px] z-40">
          <button
            onClick={restore}
            className="bg-white text-blue-700 text-[9px] font-bold px-2.5 py-1 rounded-full shadow border border-blue-200 whitespace-nowrap hover:bg-blue-50 transition-colors"
            title="Restore time logger"
          >
            {todayHours !== null ? `${todayHours % 1 === 0 ? todayHours : todayHours.toFixed(1)}h today` : '⏱ Log time'}
          </button>
        </div>
      ) : (
        <div className="fixed bottom-6 left-6 md:left-[216px] z-40 flex flex-col items-start gap-1">
          {todayHours !== null && (
            <div className="bg-white text-blue-700 text-[9px] font-bold px-2 py-0.5 rounded-full shadow border border-blue-100 whitespace-nowrap">
              {todayHours % 1 === 0 ? todayHours : todayHours.toFixed(1)}h today
            </div>
          )}
          <div className="relative">
            <button
              onClick={() => setOpen(o => !o)}
              className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95"
              title="Quick Time Log"
              aria-label="Log time"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </button>
            <button
              onClick={minimize}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-400 hover:bg-gray-600 text-white rounded-full text-[10px] font-bold leading-none flex items-center justify-center transition-colors"
              title="Hide time logger"
              aria-label="Hide time logger"
            >
              ×
            </button>
          </div>
        </div>
      )}


      {/* Panel */}
      {open && (
        <div ref={panelRef} data-tour="quick-panel" className={panelClass}>
          <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
            <span className="text-white font-semibold text-sm">Quick Time Log</span>
            <button onClick={() => setOpen(false)} className="text-white opacity-70 hover:opacity-100 text-xl leading-none">&times;</button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            {/* Date */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</label>
                <span className="text-xs font-bold text-blue-700">{weekdayLong(date)}{date === today ? ' (today)' : ''}</span>
              </div>
              <input
                type="date"
                value={date}
                min={lockBypass ? undefined : editCutoff}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setDate(e.target.value)}
                className="w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Item 44: the day is already accounted for. Said before the person
                works out what to log against a day they were on holiday. */}
            {dayAbsence && (
              <div className={`rounded-lg border px-2.5 py-2 text-[10px] leading-relaxed ${
                dayAbsence.kind === 'sickness' ? 'bg-red-50 border-red-200 text-red-800'
                : dayAbsence.kind === 'bank_holiday' ? 'bg-gray-50 border-gray-200 text-gray-600'
                : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                <strong>{dayAbsence.label}</strong> — this day is already accounted for, so there is no time to log
                against it. {dayAbsence.kind === 'bank_holiday' ? 'Log time only if you actually worked it.' : ''}
              </div>
            )}

            {/* Favourites quick-pick */}
            <FavouritesBar
              favourites={favourites} tasks={tasks} sows={sows} overheads={overheads}
              onPick={pickFavourite}
              onRemove={(fav) => toggleFav(fav.kind, fav.ref_id)}
              current={current}
              isCurrentFav={current ? isFav(current.kind, current.ref_id) : false}
              onToggleCurrent={current ? () => toggleFav(current.kind, current.ref_id, currentLabel) : null}
              theme="blue"
            />

            {/* Mode toggle */}
            <div data-tour="quick-what" className="flex rounded-lg border overflow-hidden text-sm">
              <button
                type="button"
                onClick={() => setMode('task')}
                className={`flex-1 py-1.5 font-medium transition-colors ${mode === 'task' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Task
              </button>
              <button
                type="button"
                onClick={() => setMode('sow')}
                className={`flex-1 py-1.5 font-medium transition-colors border-l border-r ${mode === 'sow' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Project
              </button>
              <button
                type="button"
                onClick={() => setMode('overhead')}
                className={`flex-1 py-1.5 font-medium transition-colors ${mode === 'overhead' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Overhead
              </button>
            </div>

            {/* Task / SOW / Overhead selector */}
            {mode === 'task' ? (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Task</label>
                <select
                  value={selectedTask}
                  onChange={e => setSelectedTask(e.target.value)}
                  className="w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">Select task...</option>
                  {tasks.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.project_name ? ` — ${t.project_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : mode === 'sow' ? (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Project</label>
                <SowSelect
                  sows={sows}
                  value={selectedSow}
                  onChange={setSelectedSow}
                  placeholder="Select project..."
                  inputClassName="w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  favouriteIds={sowFavourites}
                  onToggleFavourite={(id) => toggleFav('sow', id, sows.find(s => String(s.id) === String(id))?.reference || '')}
                />
                {isDraftSow && (
                  <div className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-2 mt-1.5 leading-relaxed">
                    <strong>Not yet issued</strong> — logged as <strong>Bid &amp; SOW Creation</strong>, still tagged to this SOW so it counts toward its cost once issued.
                  </div>
                )}

                {/* Milestone — only where the project has one to pick. Finished
                    milestones stay selectable, because late work against one is
                    real, but sit in their own group below the open ones. */}
                {!isDraftSow && milestoneExpected(milestones) && (
                  <div className="mt-2" data-tour="quick-milestone">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Milestone</label>
                    <select
                      value={selectedMilestone}
                      onChange={e => setSelectedMilestone(e.target.value)}
                      className={`w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                        milestoneUnset ? 'border-amber-400 focus:ring-amber-400' : 'focus:ring-blue-400'
                      }`}
                    >
                      <option value="">Select milestone…</option>
                      {openMilestones.map(m => (
                        <option key={m.id} value={m.id}>{milestoneLabel(m)}</option>
                      ))}
                      {doneMilestones.length > 0 && (
                        <optgroup label="Already delivered">
                          {doneMilestones.map(m => (
                            <option key={m.id} value={m.id} className="text-gray-400">
                              {milestoneLabel(m)} ({m.status})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                )}

                {/* The warning. Loud while there is no reason for it, and calmer
                    but still present once one has been written, so nobody thinks
                    the note made the entry ordinary. */}
                {milestoneUnset && (
                  <div
                    data-tour="milestone-warning"
                    className={`mt-2 rounded-lg border px-3 py-2.5 leading-relaxed ${
                      needsMilestoneReason
                        ? 'bg-amber-50 border-amber-300 text-amber-900'
                        : 'bg-amber-50/60 border-amber-200 text-amber-800'
                    }`}
                  >
                    {needsMilestoneReason ? (
                      <>
                        <div className="flex items-start gap-2">
                          <span className="text-base leading-none mt-0.5">⚠️</span>
                          <div>
                            <div className="text-[11px] font-bold uppercase tracking-wide">No milestone selected</div>
                            <div className="text-[10px] mt-1">
                              This project has milestones, and time logged against none of them
                              <strong> does not cost against the project</strong>.
                            </div>
                            <div className="text-[10px] mt-1">
                              Pick the milestone above. If there genuinely isn't one, <strong>write the reason in the
                              notes below</strong> — the entry is flagged for review.
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-[10px]">
                        <strong>No milestone</strong> — your note will be read as the reason, and this entry is flagged
                        for review on the Time Entry Audit.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Overhead category</label>
                <OverheadCategorySelect
                  categories={overheads}
                  value={selectedOverhead}
                  onChange={setSelectedOverhead}
                  className="w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            )}

            {/* Hours and the day-so-far bar, together: the tour points at them
                as one thing, and the bar is only meaningful next to the buttons
                that feed it. space-y-3 keeps the gap the form had. */}
            <div data-tour="quick-hours" className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Hours</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {quickHours.map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHours(String(h))}
                    className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                      parseFloat(hours) === h
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {h}h
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setHours(String(cap))}
                  title={`Log a full ${fmtH(cap)} day`}
                  className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                    parseFloat(hours) === cap
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-blue-200 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Full day ({fmtH(cap)})
                </button>
                {remainder > 0 && remainder !== cap && (
                  <button
                    type="button"
                    onClick={() => setHours(String(remainder))}
                    title={`Fill the rest of your ${fmtH(cap)} day`}
                    className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                      parseFloat(hours) === remainder
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-blue-200 text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    Rest of day ({fmtH(remainder)})
                  </button>
                )}
              </div>
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={hours}
                onChange={e => setHours(e.target.value)}
                className="w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="or enter hours..."
              />
            </div>

            {/* Daily cap / overtime preview */}
            <div>
              <div className="flex items-center justify-between text-[10px] font-semibold mb-1">
                <span className="text-gray-500">{fmtH(dayHours)} logged this day</span>
                <span className={dayHours + hNum > cap ? 'text-orange-600' : 'text-gray-400'}>
                  {fmtH(Math.round((dayHours + hNum) * 100) / 100)} / {fmtH(cap)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
                <div className="h-full bg-blue-500" style={{ width: `${capPct}%` }} />
                {preview.overCap && <div className="h-full bg-orange-400" style={{ width: `${Math.min(100 - capPct, Math.round((preview.overtimePart / cap) * 100))}%` }} />}
              </div>
              {preview.overCap && (
                <div className={`text-[10px] mt-1 font-semibold ${eligible ? 'text-orange-600' : 'text-gray-500'}`}>
                  {eligible
                    ? `${fmtH(preview.overtimePart)} over your ${fmtH(cap)} day → will be logged as overtime`
                    : OVERTIME_ENABLED
                      ? `${fmtH(preview.overtimePart)} over your ${fmtH(cap)} day (not eligible to claim — logged as normal)`
                      : `${fmtH(preview.overtimePart)} over your ${fmtH(cap)} day`}
                </div>
              )}
            </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                {isOther ? 'Notes (required for Other)'
                  : milestoneUnset ? 'Notes (reason required — no milestone)'
                  : 'Notes (optional)'}
              </label>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                maxLength={200}
                className={`w-full border bg-white text-gray-900 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 ${(isOther && !notes.trim()) || needsMilestoneReason ? 'border-amber-400 focus:ring-amber-400' : 'focus:ring-blue-400'}`}
                placeholder={isOther ? 'Say what this was…'
                  : milestoneUnset ? 'Why does this belong to no milestone?'
                  : 'What did you work on?'}
              />
            </div>

            {message && (
              <div className={`text-sm text-center py-1 rounded-lg ${
                message.startsWith('Logged')
                  ? 'text-green-700 bg-green-50'
                  : 'text-red-600 bg-red-50'
              }`}>
                {message}
              </div>
            )}

            {otConfirm ? (
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 space-y-2">
                <div className="text-[11px] text-orange-800 leading-snug">
                  This puts you over your <strong>{fmtH(cap)}</strong> day.
                  {otConfirm.normalPart > 0
                    ? <> Log <strong>{fmtH(otConfirm.normalPart)}</strong> as normal and <strong>{fmtH(otConfirm.overtimePart)}</strong> as overtime?</>
                    : <> Log <strong>{fmtH(otConfirm.overtimePart)}</strong> as overtime?</>}
                  <div className="text-[10px] text-orange-600 mt-1">Overtime is sent to your manager for approval.</div>
                </div>
                <button type="button" onClick={confirmSplit} disabled={submitting}
                  className="w-full py-2 bg-orange-500 text-white rounded-lg font-semibold text-sm hover:bg-orange-600 disabled:opacity-50 transition-colors">
                  {submitting ? 'Logging…' : 'Split & submit overtime'}
                </button>
                <div className="flex gap-2">
                  <button type="button" onClick={confirmAllNormal} disabled={submitting}
                    className="flex-1 py-1.5 border border-gray-300 text-gray-600 rounded-lg font-semibold text-[11px] hover:bg-white transition-colors">
                    Log all as normal
                  </button>
                  <button type="button" onClick={() => setOtConfirm(null)} disabled={submitting}
                    className="flex-1 py-1.5 border border-gray-200 text-gray-400 rounded-lg font-semibold text-[11px] hover:bg-white transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Logging...' : milestoneUnset ? 'Log without a milestone' : 'Log Time'}
              </button>
            )}
          </form>
        </div>
      )}
    </>
  );
}
